import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Clock, ArrowRight, Instagram } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "../context/LanguageContext";
import { ViberIcon, WhatsAppIcon } from "./SocialFloat";
import { VIBER_URL, WHATSAPP_URL, INSTAGRAM_URL, INSTAGRAM_HANDLE } from "../data/socials";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { t } = useLanguage();
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error(t.contact.form.error);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.contact.form.success);
      setForm({ name: "", email: "", phone: "", service: "", subject: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error(t.contact.form.error);
    } finally {
      setLoading(false);
    }
  };

  const serviceOptions = t.services.items.map((s) => s.title);

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-[#0F0F0F] py-32 lg:py-44 overflow-hidden">
      {/* Decorative gold corners */}
      <div className="absolute top-12 left-12 w-24 h-px bg-[#B08D57]/40"></div>
      <div className="absolute top-12 left-12 w-px h-24 bg-[#B08D57]/40"></div>
      <div className="absolute bottom-12 right-12 w-24 h-px bg-[#B08D57]/40"></div>
      <div className="absolute bottom-12 right-12 w-px h-24 bg-[#B08D57]/40"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 relative">
        <div className="text-center mb-20 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-7">
            <span className="w-12 h-px bg-[#B08D57]"></span>
            <span className="overline-bold text-[#B08D57]">{t.contact.overline}</span>
            <span className="w-12 h-px bg-[#B08D57]"></span>
          </div>
          <h2 className="font-display-thin text-white text-4xl sm:text-5xl lg:text-[72px] tracking-tight leading-[1.05]">
            {t.contact.title}
          </h2>
          <p className="mt-7 text-white/65 text-base sm:text-lg leading-[1.7] max-w-xl mx-auto">{t.contact.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-4">
            <div className="space-y-8">
              <InfoRow icon={Phone} label={t.contact.info.phone_label} value="+30 690 941 8914" href="tel:+306909418914" testid="contact-phone" />
              <InfoRow icon={Mail} label={t.contact.info.email_label} value="erian.calexambot@gmail.com" href="mailto:erian.calexambot@gmail.com" testid="contact-email" />
              <InfoRow icon={MapPin} label={t.contact.info.location_label} value={t.contact.info.location_value} testid="contact-location" />
              <InfoRow icon={Clock} label={t.contact.info.hours_label} value={t.contact.info.hours_value} testid="contact-hours" />

              <div className="flex flex-wrap items-center gap-3 pt-2">
                <a
                  href={WHATSAPP_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-btn"
                  className="inline-flex items-center gap-2.5 border border-white/15 text-white/80 px-5 py-3 text-[11px] tracking-[0.2em] uppercase hover:border-[#B08D57] hover:text-[#B08D57] transition-colors"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                  WhatsApp
                </a>
                <a
                  href={VIBER_URL}
                  data-testid="contact-viber-btn"
                  className="inline-flex items-center gap-2.5 border border-white/15 text-white/80 px-5 py-3 text-[11px] tracking-[0.2em] uppercase hover:border-[#B08D57] hover:text-[#B08D57] transition-colors"
                >
                  <ViberIcon className="w-4 h-4" />
                  Viber
                </a>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-instagram-btn"
                  className="inline-flex items-center gap-2.5 border border-white/15 text-white/80 px-5 py-3 text-[11px] tracking-[0.2em] uppercase hover:border-[#B08D57] hover:text-[#B08D57] transition-colors"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.4} />
                  Instagram
                </a>
              </div>
            </div>
          </div>

          <form onSubmit={onSubmit} data-testid="contact-form" className="lg:col-span-8 bg-white/[0.03] border border-white/10 p-8 sm:p-12 backdrop-blur-sm">
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-9">
              <Field label={t.contact.form.name} required>
                <input data-testid="contact-input-name" name="name" value={form.name} onChange={onChange} required className={inputCls} />
              </Field>
              <Field label={t.contact.form.email} required>
                <input data-testid="contact-input-email" type="email" name="email" value={form.email} onChange={onChange} required className={inputCls} />
              </Field>
              <Field label={t.contact.form.phone}>
                <input data-testid="contact-input-phone" name="phone" value={form.phone} onChange={onChange} className={inputCls} />
              </Field>
              <Field label={t.contact.form.service}>
                <select data-testid="contact-input-service" name="service" value={form.service} onChange={onChange} className={`${inputCls} appearance-none cursor-pointer`}>
                  <option value="" className="bg-[#0F0F0F]">{t.contact.form.service_placeholder}</option>
                  {serviceOptions.map((s) => (
                    <option key={s} value={s} className="bg-[#0F0F0F]">{s}</option>
                  ))}
                </select>
              </Field>
              <div className="sm:col-span-2">
                <Field label={t.contact.form.subject}>
                  <input data-testid="contact-input-subject" name="subject" value={form.subject} onChange={onChange} className={inputCls} />
                </Field>
              </div>
              <div className="sm:col-span-2">
                <Field label={t.contact.form.message} required>
                  <textarea data-testid="contact-input-message" name="message" value={form.message} onChange={onChange} required rows={5} className={`${inputCls} resize-none`} />
                </Field>
              </div>
            </div>
            <button
              type="submit"
              data-testid="contact-submit-btn"
              disabled={loading}
              className="mt-12 inline-flex items-center gap-4 bg-[#B08D57] text-[#0F0F0F] px-10 py-5 text-[11px] tracking-[0.3em] uppercase font-semibold hover:bg-white transition-colors disabled:opacity-50"
            >
              {loading ? t.contact.form.submitting : t.contact.cta}
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 text-white px-0 py-3 text-sm placeholder-white/30 focus:border-[#B08D57] focus:outline-none transition-colors";

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="overline text-white/55 mb-3 block">
      {label}
      {required && <span className="text-[#B08D57]"> *</span>}
    </span>
    {children}
  </label>
);

const InfoRow = ({ icon: Icon, label, value, href, testid }) => {
  const inner = (
    <div className="flex items-start gap-5">
      <Icon className="w-4 h-4 text-[#B08D57] mt-1 shrink-0" strokeWidth={1.2} />
      <div className="flex-1 min-w-0">
        <div className="overline text-white/45 mb-1.5">{label}</div>
        <div className="text-white text-sm tracking-wide">{value}</div>
      </div>
    </div>
  );
  return (
    <div data-testid={testid}>
      {href ? <a href={href} className="block hover:opacity-70 transition-opacity">{inner}</a> : inner}
    </div>
  );
};

export default Contact;
